import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SidebarCalendar from "../components/SidebarCalendar";
import TimeSelect from "../components/TimeSelect";
import ConfirmModal from "../components/modals/ConfirmModal";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import {
  clockIn,
  clockOut,
  deleteTimeClockEntry,
  getTimeClockDay,
  listOpenTimeClocks,
  upsertTimeClockEntry,
  updateTimeClockEntry,
} from "../api/timeClocks";
import { formatInTimeZone } from "../utils/datetime";
import { formatDateOnlyYYYYMMDD } from "../utils/dateOnly";
import { getStaffSchedule } from "../api/staffs";
import "./HrSectionCompact.css";

const WEEKDAY_SHORT_TO_INDEX = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

const WEEKDAY_LABELS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const parseDateParts = (value) => {
  if (!value || typeof value !== "string") return null;
  const [yearStr, monthStr, dayStr] = value.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  return { year, month, day };
};

const formatDatePartsInTimeZone = (date, timeZone) => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(date);
};

const getScheduleDayIndex = (workDate, timeZone) => {
  const parts = parseDateParts(workDate);
  if (!parts) return null;
  const base = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0));
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
    });
    let adjusted = base;
    let attempts = 0;
    while (attempts < 2) {
      const localDate = formatDatePartsInTimeZone(adjusted, timeZone);
      if (localDate === workDate) {
        const weekday = formatter.format(adjusted);
        return WEEKDAY_SHORT_TO_INDEX[weekday] ?? null;
      }
      const direction = localDate < workDate ? 1 : -1;
      adjusted = new Date(adjusted.getTime() + direction * 24 * 60 * 60 * 1000);
      attempts += 1;
    }
    const weekday = formatter.format(base);
    return WEEKDAY_SHORT_TO_INDEX[weekday] ?? null;
  } catch (err) {
    const utcDay = new Date(
      Date.UTC(parts.year, parts.month - 1, parts.day)
    ).getUTCDay();
    return (utcDay + 6) % 7;
  }
};

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toCalendarDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return new Date();
  const parts = dateStr.split("-").map((value) => Number(value));
  if (parts.length !== 3 || parts.some((value) => Number.isNaN(value))) {
    return new Date();
  }
  const [year, month, day] = parts;
  return new Date(year, month - 1, day, 12, 0, 0);
};

const formatDisplayTime = (value, timeZone) =>
  formatInTimeZone(
    value,
    timeZone,
    {
      hour: "numeric",
      minute: "2-digit",
    },
    "en-US"
  );

const formatEditTime = (value, timeZone) => {
  const formatted = formatInTimeZone(
    value,
    timeZone,
    {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    },
    "en-GB"
  );
  if (!formatted || formatted === "-") return "";
  const match = formatted.match(/(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : "";
};

const timeStringToMinutes = (value) => {
  if (!value || typeof value !== "string") return null;
  const [hourStr, minuteStr] = value.split(":");
  const hours = Number(hourStr);
  const minutes = Number(minuteStr);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

const HELP_SECTIONS = [
  {
    id: "overview",
    title: "What this page does",
    description: "Track daily staff time entries for payroll and attendance.",
    anchorId: "tc-overview",
    bullets: [
      "Pick a work date from the calendar.",
      "Review open time clocks and unresolved entries.",
      "Use Clock In/Out or Day Worked based on pay mode.",
    ],
    tips: [
      "Use Day Worked for daily pay staff.",
      "Use Clock In and Clock Out for hourly staff.",
    ],
  },
  {
    id: "date-panel",
    title: "Date and open clocks",
    description: "Choose the correct work date and check open entries before editing.",
    anchorId: "tc-date",
    bullets: [
      "Calendar controls the active work date.",
      "Open Time Clocks list shows dates with open entries.",
    ],
    tips: ["Resolve open entries daily to keep payroll accurate."],
  },
  {
    id: "entries",
    title: "Daily entries",
    description: "Edit staff time rows for the selected date.",
    anchorId: "tc-entries",
    bullets: [
      "Use Set Time to edit in/out values manually.",
      "Save Day Worked values between 0 and 1.",
      "Row notices confirm successful updates.",
    ],
    tips: ["Do not use Day Worked and Clock In/Out for the same row."],
  },
  {
    id: "actions",
    title: "Clock actions",
    description: "Use action buttons to manage the staff row quickly.",
    anchorId: "tc-actions",
    bullets: [
      "Clock In and Clock Out are available for today only.",
      "Delete clears existing time entries when corrections are needed.",
    ],
    tips: ["If actions are disabled, check whether Day Worked is already set."],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "Cannot clock in or out",
    body: "Clear Day Worked first, then use Clock In/Out.",
  },
  {
    title: "Cannot set Day Worked",
    body: "Delete existing time entry for that row, then enter Day Worked.",
  },
  {
    title: "No staff shown",
    body: "Only staff with Uses Time Clock enabled are listed.",
  },
];

const TOOLTIPS = {
  select_date: "Select the work date for this time clock sheet.",
  open_clocks: "Dates that still have open clock entries.",
  clock_in: "Clock in timestamp for hourly staff.",
  clock_out: "Clock out timestamp for hourly staff.",
  day_worked: "Use 0 to 1 for daily pay staff.",
  actions: "Row actions for editing, stamping, and deleting.",
};

export default function TimeClock() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [selectedWorkDate, setSelectedWorkDate] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dayData, setDayData] = useState(null);
  const [openList, setOpenList] = useState([]);
  const [loadingDay, setLoadingDay] = useState(false);
  const [loadingOpen, setLoadingOpen] = useState(false);
  const [error, setError] = useState("");
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [editValues, setEditValues] = useState({
    clock_in_time: "",
    clock_out_time: "",
    day_work_ratio: "",
  });
  const [draftDayWorkedByStaff, setDraftDayWorkedByStaff] = useState({});
  const [dayWorkedErrors, setDayWorkedErrors] = useState({});
  const [dayWorkedSaving, setDayWorkedSaving] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [rowError, setRowError] = useState("");
  const [rowErrors, setRowErrors] = useState({});
  const [rowNotices, setRowNotices] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmEntry, setDeleteConfirmEntry] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [staffScheduleCache, setStaffScheduleCache] = useState({});
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);

  const salonTimeZone =
    user?.salon_timezone || dayData?.salon_timezone || "America/Toronto";

  const todayWorkDate = useMemo(
    () => formatDateISO(new Date(), salonTimeZone),
    [salonTimeZone]
  );

  useEffect(() => {
    if (!selectedWorkDate) {
      const today = formatDateISO(new Date(), salonTimeZone);
      setSelectedWorkDate(today);
      setSelectedDate(toCalendarDate(today));
    }
  }, [salonTimeZone, selectedWorkDate]);

  useEffect(() => {
    if (selectedWorkDate) {
      setSelectedDate(toCalendarDate(selectedWorkDate));
    }
  }, [selectedWorkDate]);

  const loadDay = useCallback(
    async (workDate) => {
      if (!workDate) return;
      setLoadingDay(true);
      setError("");
      try {
        const data = await getTimeClockDay(workDate, token);
        setDayData(data);
      } catch (err) {
        setDayData(null);
        setError(
          err?.response?.data?.detail || "Unable to load time clock entries."
        );
      } finally {
        setLoadingDay(false);
      }
    },
    [token]
  );

  const loadOpenList = useCallback(async () => {
    setLoadingOpen(true);
    try {
      const data = await listOpenTimeClocks(token);
      setOpenList(Array.isArray(data) ? data : []);
    } catch (err) {
      setOpenList([]);
    } finally {
      setLoadingOpen(false);
    }
  }, [token]);

  useEffect(() => {
    if (!selectedWorkDate) return;
    loadDay(selectedWorkDate);
  }, [selectedWorkDate, loadDay]);

  useEffect(() => {
    loadOpenList();
  }, [loadOpenList]);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    },
    []
  );

  const refreshAll = useCallback(async () => {
    await Promise.all([loadDay(selectedWorkDate), loadOpenList()]);
  }, [loadDay, loadOpenList, selectedWorkDate]);

  const staffList = Array.isArray(dayData?.staff) ? dayData.staff : [];
  const entries = Array.isArray(dayData?.entries) ? dayData.entries : [];
  const entriesByStaff = useMemo(() => {
    const map = new Map();
    entries.forEach((entry) => {
      if (entry?.staff_id != null) {
        map.set(entry.staff_id, entry);
      }
    });
    return map;
  }, [entries]);

  useEffect(() => {
    if (!selectedWorkDate) return;
    const nextDrafts = {};
    staffList.forEach((staff) => {
      const entry = entriesByStaff.get(staff.id);
      const ratioValue = Number(entry?.day_work_ratio);
      nextDrafts[staff.id] =
        Number.isFinite(ratioValue) && ratioValue > 0 ? String(ratioValue) : "";
    });
    setDraftDayWorkedByStaff(nextDrafts);
    setDayWorkedErrors({});
  }, [selectedWorkDate, staffList, entriesByStaff]);

  const permissions = dayData?.permissions || {};
  const canEdit = Boolean(permissions?.can_edit);
  const canDelete = Boolean(permissions?.can_delete);
  const isToday = selectedWorkDate === todayWorkDate;

  const handleCalendarChange = (date) => {
    const nextDate = Array.isArray(date) ? date[0] : date;
    const iso = formatDateISO(nextDate, salonTimeZone);
    setSelectedWorkDate(iso);
  };

  const setRowErrorMessage = (staffId, message) => {
    if (!staffId) return;
    setRowErrors((prev) => ({ ...prev, [staffId]: message }));
  };

  const clearRowErrorMessage = (staffId) => {
    if (!staffId) return;
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[staffId];
      return next;
    });
  };

  const setRowNoticeMessage = (staffId, message) => {
    if (!staffId) return;
    setRowNotices((prev) => ({ ...prev, [staffId]: message }));
  };

  const clearRowNoticeMessage = (staffId) => {
    if (!staffId) return;
    setRowNotices((prev) => {
      const next = { ...prev };
      delete next[staffId];
      return next;
    });
  };

  const setDayWorkedError = (staffId, message) => {
    if (!staffId) return;
    setDayWorkedErrors((prev) => ({ ...prev, [staffId]: message }));
  };

  const clearDayWorkedError = (staffId) => {
    if (!staffId) return;
    setDayWorkedErrors((prev) => {
      const next = { ...prev };
      delete next[staffId];
      return next;
    });
  };

  const beginEdit = async (staffId, useSchedule = false) => {
    const entry = entriesByStaff.get(staffId);
    setRowError("");
    clearRowErrorMessage(staffId);
    clearRowNoticeMessage(staffId);
    const ratioValue = Number(entry?.day_work_ratio);
    if (Number.isFinite(ratioValue) && ratioValue > 0) {
      setRowError(
        "Clock In/Out disabled because Day Worked is set. Clear Day Worked first."
      );
      return;
    }
    setEditingStaffId(staffId);
    if (entry) {
      const ratioValue = Number(entry?.day_work_ratio);
      setEditValues({
        clock_in_time: entry ? formatEditTime(entry.clock_in_at, salonTimeZone) : "",
        clock_out_time: entry ? formatEditTime(entry.clock_out_at, salonTimeZone) : "",
        day_work_ratio:
          Number.isFinite(ratioValue) && ratioValue > 0 ? String(ratioValue) : "",
      });
      return;
    }
    if (!useSchedule) {
      setEditValues({ clock_in_time: "", clock_out_time: "", day_work_ratio: "" });
      return;
    }
    setEditValues({ clock_in_time: "", clock_out_time: "", day_work_ratio: "" });
    let weeklySchedule = staffScheduleCache[staffId];
    if (!weeklySchedule) {
      try {
        const scheduleData = await getStaffSchedule(staffId, token);
        weeklySchedule = Array.isArray(scheduleData?.weekly_schedule)
          ? scheduleData.weekly_schedule
          : [];
        setStaffScheduleCache((prev) => ({
          ...prev,
          [staffId]: weeklySchedule,
        }));
      } catch (err) {
        weeklySchedule = [];
      }
    }
    const dayIndex = getScheduleDayIndex(selectedWorkDate, salonTimeZone);
    const dayLabel = dayIndex != null ? WEEKDAY_LABELS[dayIndex] : "this day";
    const scheduleForDay =
      dayIndex != null
        ? weeklySchedule.find(
          (item) => Number(item.day_of_week) === dayIndex
        )
        : null;
    if (
      scheduleForDay &&
      scheduleForDay.is_working &&
      scheduleForDay.start_time &&
      scheduleForDay.end_time
    ) {
      setEditValues({
        clock_in_time: scheduleForDay.start_time,
        clock_out_time: scheduleForDay.end_time,
        day_work_ratio: "",
      });
      return;
    }
    setRowNoticeMessage(
      staffId,
      `No working hours set for ${dayLabel}. Please choose times manually.`
    );
  };

  const cancelEdit = () => {
    if (editingStaffId) {
      clearRowErrorMessage(editingStaffId);
      clearRowNoticeMessage(editingStaffId);
    }
    setEditingStaffId(null);
    setEditValues({ clock_in_time: "", clock_out_time: "", day_work_ratio: "" });
    setRowError("");
  };

  const handleClockIn = async (staffId) => {
    setRowError("");
    const entry = entriesByStaff.get(staffId);
    const ratioValue = Number(entry?.day_work_ratio);
    if (Number.isFinite(ratioValue) && ratioValue > 0) {
      setRowError(
        "Clock In/Out disabled because Day Worked is set. Clear Day Worked first."
      );
      return;
    }
    try {
      await clockIn(staffId, selectedWorkDate, token);
      await refreshAll();
    } catch (err) {
      setRowError(
        err?.response?.data?.detail || "Unable to clock in. Please try again."
      );
    }
  };

  const handleClockOut = async (staffId) => {
    setRowError("");
    const entry = entriesByStaff.get(staffId);
    const ratioValue = Number(entry?.day_work_ratio);
    if (Number.isFinite(ratioValue) && ratioValue > 0) {
      setRowError(
        "Clock In/Out disabled because Day Worked is set. Clear Day Worked first."
      );
      return;
    }
    try {
      await clockOut(staffId, selectedWorkDate, token);
      await refreshAll();
    } catch (err) {
      setRowError(
        err?.response?.data?.detail || "Unable to clock out. Please try again."
      );
    }
  };

  const handleSaveEdit = async (staffId) => {
    const entry = entriesByStaff.get(staffId);
    setRowError("");
    const savedRatioValue = Number(entry?.day_work_ratio);
    if (
      Number.isFinite(savedRatioValue) &&
      savedRatioValue > 0 &&
      (editValues.clock_in_time || editValues.clock_out_time)
    ) {
      setRowErrorMessage(
        staffId,
        "Cannot set Clock In/Out when Day Worked is set. Clear Day Worked first."
      );
      return;
    }
    const ratioText = String(editValues.day_work_ratio ?? "").trim();
    const ratioValue = ratioText === "" ? null : Number(ratioText);
    if (
      ratioText !== "" &&
      (!Number.isFinite(ratioValue) || ratioValue < 0 || ratioValue > 1)
    ) {
      setRowErrorMessage(staffId, "Day worked must be between 0 and 1.");
      return;
    }
    const hasTimes = Boolean(editValues.clock_in_time || editValues.clock_out_time);
    const hasRatio = ratioValue != null && ratioValue > 0;
    if (!entry && !hasTimes && !hasRatio) {
      setRowErrorMessage(
        staffId,
        "Enter clock times or a day worked ratio."
      );
      return;
    }
    const inMinutes = timeStringToMinutes(editValues.clock_in_time);
    const outMinutes = timeStringToMinutes(editValues.clock_out_time);
    if (
      inMinutes != null &&
      outMinutes != null &&
      outMinutes < inMinutes
    ) {
      setRowErrorMessage(
        staffId,
        "Clock out time cannot be earlier than clock in time."
      );
      return;
    }
    setSavingEdit(true);
    try {
      const payload = {};
      if (selectedWorkDate) payload.work_date = selectedWorkDate;
      if (editValues.clock_in_time) {
        payload.clock_in_time = editValues.clock_in_time;
      }
      if (editValues.clock_out_time) {
        payload.clock_out_time = editValues.clock_out_time;
      }
      payload.day_work_ratio = ratioValue === null ? null : ratioValue;
      if (entry?.id) {
        await updateTimeClockEntry(entry.id, payload, token);
      } else {
        await upsertTimeClockEntry(staffId, payload, token);
      }
      cancelEdit();
      clearRowErrorMessage(staffId);
      clearRowNoticeMessage(staffId);
      await refreshAll();
    } catch (err) {
      setRowError(
        err?.response?.data?.detail || err?.message || "Unable to save changes."
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSaveDayWorked = async (staffId, nextValue) => {
    if (!staffId || !selectedWorkDate || !canEdit) return;
    const entry = entriesByStaff.get(staffId);
    if (entry?.clock_in_at || entry?.clock_out_at) {
      setDayWorkedError(
        staffId,
        "Cannot set Day Worked when Clock In/Out exists. Delete the time entry first."
      );
      return;
    }
    const draftValue =
      nextValue !== undefined ? nextValue : draftDayWorkedByStaff?.[staffId] ?? "";
    const trimmed = String(draftValue).trim();
    const ratioValue = trimmed === "" ? null : Number(trimmed);
    if (
      trimmed !== "" &&
      (!Number.isFinite(ratioValue) || ratioValue < 0 || ratioValue > 1)
    ) {
      setDayWorkedError(staffId, "Day worked must be between 0 and 1.");
      return;
    }
    clearDayWorkedError(staffId);
    setDayWorkedSaving((prev) => ({ ...prev, [staffId]: true }));
    try {
      await upsertTimeClockEntry(
        staffId,
        {
          work_date: selectedWorkDate,
          day_work_ratio: ratioValue === null ? null : ratioValue,
        },
        token
      );
      await refreshAll();
    } catch (err) {
      setDayWorkedError(
        staffId,
        err?.response?.data?.detail || "Unable to save day worked."
      );
    } finally {
      setDayWorkedSaving((prev) => ({ ...prev, [staffId]: false }));
    }
  };

  const handleClearDayWorked = async (staffId) => {
    setDraftDayWorkedByStaff((prev) => ({ ...prev, [staffId]: "" }));
    await handleSaveDayWorked(staffId, "");
  };

  const handleDeleteEntry = async (entryId) => {
    if (!entryId || !canDelete) return;
    setDeleteSubmitting(true);
    setRowError("");
    try {
      await deleteTimeClockEntry(entryId, token);
      await refreshAll();
      setDeleteConfirmOpen(false);
      setDeleteConfirmEntry(null);
    } catch (err) {
      setRowError(
        err?.response?.data?.detail || "Unable to delete entry."
      );
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const openDeleteConfirm = (entry) => {
    if (!entry?.id || !canDelete) return;
    setRowError("");
    setDeleteConfirmEntry(entry);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (deleteSubmitting) return;
    setDeleteConfirmOpen(false);
    setDeleteConfirmEntry(null);
  };

  const headerDateLabel = selectedWorkDate
    ? formatDateOnlyYYYYMMDD(selectedWorkDate)
    : "";
  const salonName = user?.salon_name || user?.salonName || "";
  const modalText = "text-sm md:text-base";

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const jumped = scrollToSection(id);
      if (!jumped) return;
      setHighlightedSectionId(id);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedSectionId((current) => (current === id ? "" : current));
      }, 1500);
    },
    [scrollToSection]
  );

  const getSectionClassName = useCallback(
    (id, baseClassName) =>
      `${baseClassName} ${
        highlightedSectionId === id
          ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
          : ""
      }`,
    [highlightedSectionId]
  );

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/hr/training");
  };

  return (
    <div className="hrSectionShell">
      <div className={`hrSectionWide hr-section-compact ${modalText}`}>
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Time Clock
            </h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div id="tc-overview">
              <ReportHelpCard
                title="Help Guide: Time Clock"
                subtitle="Track staff in/out entries and day worked values for payroll."
                sections={HELP_SECTIONS}
                troubleshooting={TROUBLESHOOTING_ITEMS}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            {rowError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {rowError}
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
              <div
                id="tc-date"
                className={getSectionClassName(
                  "tc-date",
                  "rounded-2xl border border-slate-200 bg-white p-4 space-y-3"
                )}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                      Select Date
                    </h2>
                    <InfoTooltip text={TOOLTIPS.select_date} label="Select Date" />
                  </div>
                  <p className="text-sm text-slate-500">
                    Choose a work date to view or update entries.
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white overflow-visible">
                  <SidebarCalendar
                    selectedDate={selectedDate}
                    onChange={handleCalendarChange}
                    showCustomViewToggle={false}
                    showHideNotWorkingToggle={false}
                    showOnMobile
                    className="border-none bg-white w-full min-w-0 lg:w-full lg:min-w-0"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center gap-2">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Open Time Clocks
                      </h3>
                      <InfoTooltip text={TOOLTIPS.open_clocks} label="Open Time Clocks" />
                    </div>
                    {loadingOpen && (
                      <span className="text-xs text-slate-500">Loading...</span>
                    )}
                  </div>
                  <div className="space-y-2 max-h-40 overflow-auto pr-1">
                    {openList.length === 0 && !loadingOpen && (
                      <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                        No open entries found.
                      </div>
                    )}
                    {openList.map((item) => {
                      const dateValue = item?.work_date;
                      const label = formatDateOnlyYYYYMMDD(String(dateValue || ""));
                      const isActive = dateValue === selectedWorkDate;
                      return (
                        <button
                          key={dateValue}
                          type="button"
                          onClick={() => setSelectedWorkDate(String(dateValue))}
                          className={`w-full rounded-xl border px-3 py-2 text-left text-sm transition ${
                            isActive
                              ? "border-slate-900 bg-slate-900 text-white"
                              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{label}</span>
                            <span className="text-xs">
                              {item?.open_count ?? 0}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div
                id="tc-entries"
                className={getSectionClassName(
                  "tc-entries",
                  "rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 lg:p-6 space-y-4 min-w-0"
                )}
              >
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                      Time Clock for {headerDateLabel}
                    </h2>
                    <p className="text-sm text-slate-500">
                      Staff listed below have Uses Time Clock enabled.
                    </p>
                    <p className="text-xs text-slate-500">
                      For daily pay, enter Day Worked (0-1). For hourly pay, use Clock In/Out.
                    </p>
                  </div>
                  {!isToday && (
                    <div className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                      Stamping is limited to today.
                    </div>
                  )}
                </div>

                <div
                  id="tc-actions"
                  className={getSectionClassName(
                    "tc-actions",
                    "mt-3 rounded-2xl border border-slate-200 bg-white overflow-x-auto lg:overflow-visible"
                  )}
                >
                  <table className="w-full table-fixed text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                      <tr>
                        <th className="px-3 py-2 text-left w-40">Employee</th>
                        <th className="px-3 py-2 text-left w-48">
                          <span className="inline-flex items-center gap-2">
                            In
                            <InfoTooltip text={TOOLTIPS.clock_in} label="Clock In" />
                          </span>
                        </th>
                        <th className="px-3 py-2 text-left w-48">
                          <span className="inline-flex items-center gap-2">
                            Out
                            <InfoTooltip text={TOOLTIPS.clock_out} label="Clock Out" />
                          </span>
                        </th>
                        <th className="px-3 py-2 text-left w-32">
                          <span className="inline-flex items-center gap-2">
                            Day Worked
                            <InfoTooltip text={TOOLTIPS.day_worked} label="Day Worked" />
                          </span>
                        </th>
                        <th className="px-3 py-2 text-right w-44">
                          <span className="inline-flex items-center justify-end gap-2">
                            Actions
                            <InfoTooltip text={TOOLTIPS.actions} label="Actions" />
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {loadingDay && (
                        <tr>
                          <td className="px-3 py-4 text-slate-500" colSpan={5}>
                            Loading time clock entries...
                          </td>
                        </tr>
                      )}
                      {!loadingDay && staffList.length === 0 && (
                        <tr>
                          <td className="px-3 py-4 text-slate-500" colSpan={5}>
                            No staff are enabled for time clock.
                          </td>
                        </tr>
                      )}
                      {!loadingDay &&
                        staffList.map((staff) => {
                          const entry = entriesByStaff.get(staff.id);
                          const hasEntry = Boolean(entry);
                          const isEditing = editingStaffId === staff.id;
                          const canClockIn = !entry?.clock_in_at && isToday;
                          const canClockOut =
                            entry?.clock_in_at && !entry?.clock_out_at && isToday;
                          const hasTime = Boolean(
                            entry?.clock_in_at || entry?.clock_out_at
                          );
                          const savedRatioValue = Number(entry?.day_work_ratio);
                          const savedRatio = Number.isFinite(savedRatioValue)
                            ? savedRatioValue
                            : 0;
                          const hasDayWorked = savedRatio > 0;
                          const draftValue = draftDayWorkedByStaff?.[staff.id] ?? "";
                          const draftTrimmed = String(draftValue).trim();
                          const draftNumber =
                            draftTrimmed === "" ? 0 : Number(draftTrimmed);
                          const draftComparable = Number.isFinite(draftNumber)
                            ? draftNumber
                            : null;
                          const isDayWorkedDirty =
                            draftComparable !== null
                              ? Math.abs(draftNumber - savedRatio) > 0.0001
                              : draftTrimmed !== "" || savedRatio !== 0;
                          const dayWorkedLocked = hasTime || isEditing;
                          const dayWorkedError = dayWorkedErrors?.[staff.id];
                          const isDayWorkedSaving = Boolean(
                            dayWorkedSaving?.[staff.id]
                          );
                          const dayWorkedDisabledReason =
                            "Clock In/Out disabled because Day Worked is set. Clear Day Worked first.";
                          const rowErrorMessage = rowErrors[staff.id];
                          const rowNoticeMessage = rowNotices[staff.id];
                          return (
                            <tr key={staff.id} className="hover:bg-slate-50">
                              <td className="px-3 py-2.5 font-semibold text-slate-900 whitespace-nowrap">
                                {staff.display_name || `Staff #${staff.id}`}
                              </td>
                              <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                                {isEditing ? (
                                  <TimeSelect
                                    value={editValues.clock_in_time ?? ""}
                                    allowEmpty
                                    compact
                                    onChange={(val) => {
                                      clearRowErrorMessage(staff.id);
                                      clearRowNoticeMessage(staff.id);
                                      setEditValues((prev) => ({
                                        ...prev,
                                        clock_in_time: val,
                                      }));
                                    }}
                                  />
                                ) : entry?.clock_in_at ? (
                                  <button
                                    type="button"
                                    onClick={() => canEdit && beginEdit(staff.id, false)}
                                    className={`text-xs font-semibold ${
                                      canEdit
                                        ? "text-slate-700 hover:text-slate-900"
                                        : "text-slate-700"
                                    }`}
                                    disabled={!canEdit}
                                  >
                                    {formatDisplayTime(entry.clock_in_at, salonTimeZone)}
                                  </button>
                                ) : (
                                  <span className="text-slate-400">--</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                                {isEditing ? (
                                  <TimeSelect
                                    value={editValues.clock_out_time ?? ""}
                                    allowEmpty
                                    compact
                                    onChange={(val) => {
                                      clearRowErrorMessage(staff.id);
                                      clearRowNoticeMessage(staff.id);
                                      setEditValues((prev) => ({
                                        ...prev,
                                        clock_out_time: val,
                                      }));
                                    }}
                                  />
                                ) : entry?.clock_out_at ? (
                                  <button
                                    type="button"
                                    onClick={() => canEdit && beginEdit(staff.id, false)}
                                    className={`text-xs font-semibold ${
                                      canEdit
                                        ? "text-slate-700 hover:text-slate-900"
                                        : "text-slate-700"
                                    }`}
                                    disabled={!canEdit}
                                  >
                                    {formatDisplayTime(entry.clock_out_at, salonTimeZone)}
                                  </button>
                                ) : (
                                  <span className="text-slate-400">--</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-slate-700">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    placeholder="0-1"
                                    value={editValues.day_work_ratio ?? ""}
                                    onChange={(e) => {
                                      clearRowErrorMessage(staff.id);
                                      clearRowNoticeMessage(staff.id);
                                      setEditValues((prev) => ({
                                        ...prev,
                                        day_work_ratio: e.target.value,
                                      }));
                                    }}
                                    disabled={dayWorkedLocked || !canEdit}
                                    className={`w-full rounded-lg border px-2 py-1 text-xs ${
                                      dayWorkedLocked || !canEdit
                                        ? "border-slate-200 bg-slate-50 text-slate-400"
                                        : "border-slate-200 text-slate-700"
                                    }`}
                                  />
                                ) : (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <div
                                        title={
                                          hasTime
                                            ? "Day Worked disabled because time entry exists. Delete entry to use Day Worked."
                                            : undefined
                                        }
                                        tabIndex={hasTime ? 0 : undefined}
                                        aria-label={
                                          hasTime
                                            ? "Day Worked disabled because time entry exists. Delete entry to use Day Worked."
                                            : undefined
                                        }
                                        className={
                                          hasTime
                                            ? "w-full focus:outline-none focus:ring-2 focus:ring-slate-300"
                                            : "w-full"
                                        }
                                      >
                                        <input
                                          type="number"
                                          min="0"
                                          max="1"
                                          step="0.1"
                                          placeholder="0-1"
                                          value={draftValue}
                                          onChange={(e) => {
                                            clearDayWorkedError(staff.id);
                                            setDraftDayWorkedByStaff((prev) => ({
                                              ...prev,
                                              [staff.id]: e.target.value,
                                            }));
                                          }}
                                          disabled={dayWorkedLocked || !canEdit || isDayWorkedSaving}
                                          className={`w-full rounded-lg border px-2 py-1 text-xs ${
                                            dayWorkedLocked || !canEdit
                                              ? "border-slate-200 bg-slate-50 text-slate-400"
                                              : "border-slate-200 text-slate-700"
                                          }`}
                                        />
                                      </div>
                                      {hasDayWorked && !hasTime && canEdit && (
                                        <button
                                          type="button"
                                          onClick={() => handleClearDayWorked(staff.id)}
                                          disabled={isDayWorkedSaving}
                                          className="text-[10px] font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-60"
                                        >
                                          Clear
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-right align-top">
                                {isEditing ? (
                                  <div className="flex flex-col items-end gap-1">
                                    <div className="flex flex-wrap items-center justify-end gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleSaveEdit(staff.id)}
                                        disabled={savingEdit}
                                        className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                                      >
                                        {savingEdit ? "Saving..." : "Save"}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={cancelEdit}
                                        className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                    {rowNoticeMessage && (
                                      <div className="text-xs text-slate-500 whitespace-normal break-words text-right max-w-[220px]">
                                        {rowNoticeMessage}
                                      </div>
                                    )}
                                    {rowErrorMessage && (
                                      <div className="text-xs text-rose-600 whitespace-normal break-words text-right max-w-[220px]">
                                        {rowErrorMessage}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-end gap-1">
                                    <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
                                    {canClockIn && (
                                      hasDayWorked ? (
                                        <div
                                          title={dayWorkedDisabledReason}
                                          tabIndex={0}
                                          aria-label={dayWorkedDisabledReason}
                                          className="inline-flex focus:outline-none focus:ring-2 focus:ring-slate-300 rounded-full"
                                        >
                                          <button
                                            type="button"
                                            onClick={() => handleClockIn(staff.id)}
                                            className="cursor-not-allowed rounded-full bg-slate-300 px-3 py-1 text-xs font-semibold text-white"
                                          >
                                            Clock In
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => handleClockIn(staff.id)}
                                          className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800"
                                        >
                                          Clock In
                                        </button>
                                      )
                                    )}
                                    {canClockOut && (
                                      hasDayWorked ? (
                                        <div
                                          title={dayWorkedDisabledReason}
                                          tabIndex={0}
                                          aria-label={dayWorkedDisabledReason}
                                          className="inline-flex focus:outline-none focus:ring-2 focus:ring-slate-300 rounded-full"
                                        >
                                          <button
                                            type="button"
                                            onClick={() => handleClockOut(staff.id)}
                                            className="cursor-not-allowed rounded-full bg-slate-300 px-3 py-1 text-xs font-semibold text-white"
                                          >
                                            Clock Out
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => handleClockOut(staff.id)}
                                          className="rounded-full bg-slate-700 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800"
                                        >
                                          Clock Out
                                        </button>
                                      )
                                    )}
                                    {canEdit && isDayWorkedDirty && !hasTime && (
                                      <button
                                        type="button"
                                        onClick={() => handleSaveDayWorked(staff.id)}
                                        disabled={isDayWorkedSaving}
                                          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                                        >
                                          {isDayWorkedSaving ? "Saving..." : "Save"}
                                        </button>
                                      )}
                                    {!hasEntry && canEdit && (
                                      hasDayWorked ? (
                                        <div
                                          title={dayWorkedDisabledReason}
                                          tabIndex={0}
                                          aria-label={dayWorkedDisabledReason}
                                          className="inline-flex focus:outline-none focus:ring-2 focus:ring-slate-300 rounded-full"
                                        >
                                          <button
                                            type="button"
                                            onClick={() => beginEdit(staff.id, true)}
                                            className="cursor-not-allowed text-xs font-semibold text-slate-400"
                                          >
                                            Set Time
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => beginEdit(staff.id, true)}
                                          className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                                        >
                                          Set Time
                                        </button>
                                      )
                                    )}
                                    {hasEntry && canEdit && (
                                      hasDayWorked ? (
                                        <div
                                          title={dayWorkedDisabledReason}
                                          tabIndex={0}
                                          aria-label={dayWorkedDisabledReason}
                                          className="inline-flex focus:outline-none focus:ring-2 focus:ring-slate-300 rounded-full"
                                        >
                                          <button
                                            type="button"
                                            onClick={() => beginEdit(staff.id, false)}
                                            className="cursor-not-allowed text-xs font-semibold text-slate-400"
                                          >
                                            Edit
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => beginEdit(staff.id, false)}
                                          className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                                        >
                                          Edit
                                        </button>
                                      )
                                    )}
                                      {hasEntry && canDelete && (
                                        <button
                                          type="button"
                                          onClick={() => openDeleteConfirm(entry)}
                                          className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                                        >
                                          Delete
                                        </button>
                                      )}
                                      {!canClockIn &&
                                        !canClockOut &&
                                        !hasEntry &&
                                        !canEdit && (
                                          <span className="text-xs text-slate-400">
                                            No actions
                                          </span>
                                        )}
                                    </div>
                                    {dayWorkedError && (
                                      <div className="text-xs text-rose-600 whitespace-normal break-words text-right max-w-[220px]">
                                        {dayWorkedError}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        open={deleteConfirmOpen}
        title="Confirm"
        message="Are you sure you want to delete this time entry?"
        confirmText={deleteSubmitting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        onClose={closeDeleteConfirm}
        onConfirm={() => handleDeleteEntry(deleteConfirmEntry?.id)}
        isBusy={deleteSubmitting}
      />
    </div>
  );
}
